// INV31 (docs/build-briefs/INV31-set-starting-stock.md; DEC-288). Pure, no
// DOM (see test/ui-has-permission.test.ts's own header for why a plain
// node:test file can import a ui/src module directly): the logic the "Set
// starting stock" screen trusts. How an item is classified, what an entry
// means, what each kind sends to INV7's opening-balance route, the filters and
// counts, the per-person draft, what each server answer means, the batch
// runner and the setup step's state. The screen itself is
// ui/src/startingStock.ts.
//
// The unit rules are not guesses. They were read in
// src/domain/inventoryUnitConversion.ts (resolveCanonicalQuantity) and are
// exercised by test/inv31-opening-balance-sent-units.test.ts:
//   - a sized bottle, wine or keg sends the product's OWN container_type as
//     the unit, so a complete tenant converts it to ounces and an open tenant
//     stores it as sent;
//   - a keg that carries only a keg-size label has no size to convert, so it is
//     ounces only, the same as an item with no bottle size;
//   - a whole-unit item sends "unit";
//   - an item with no usable size sends "oz".

import type { LiquorProductDto, OpeningBalanceHistory } from "./api.ts";
import { resolveOuncesPerContainer } from "../../src/domain/inventorySizeUnits.ts";
import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import { categoryLabel } from "./catalogDisplay.ts";
import { BOTTLE_TENTH_OPTIONS, combineWholeBottlesAndTenths } from "./countWalk.ts";
import { compareNames } from "./nameSort.ts";

/** One constant note, so nobody types a reason (no reason vocabulary applies
 * to an opening balance). */
export const OPENING_BALANCE_NOTE = "Starting stock, entered at setup";

/** The client mirror of the server's quantity rule: non-negative, up to four
 * decimal places (the same shape as admin.ts's COUNT_QUANTITY_PATTERN). */
const QUANTITY_PATTERN = /^\d+(\.\d{1,4})?$/;

export type StockKind = "bottle" | "keg" | "unit" | "unsized";

export interface StockItem {
  id: string;
  name: string;
  categoryKey: string;
  categoryName: string;
  kind: StockKind;
  /** The unit word for a bottle or keg row ("bottle", "keg", or another
   * container type). Empty for the other kinds. */
  containerWord: string;
  /** The unit sent for a container quantity: the product's own container_type.
   * Null for a unit or ounces-only row. */
  containerUnit: string | null;
  sizeLabel: string;
  ouncesPerContainer: number | null;
  /** The second line of the row: category plus size. */
  meta: string;
}

/** The shared ounces-per-container rule, as a positive number or null. Typed text the shared parser refuses (more than
 * four decimals, letters) is simply "no usable size", the same answer an empty field gives. */
function containerOunces(
  product: {
    container_type: string | null;
    size_value: string | null;
    size_unit: string | null;
    default_keg_size: string | null;
  },
  kegVolumeOzByValue: ReadonlyMap<string, string>,
): number | null {
  let text: string | null;
  try {
    text = resolveOuncesPerContainer(product, kegVolumeOzByValue);
  } catch {
    return null;
  }
  const ounces = text === null ? null : Number(text);
  return ounces !== null && Number.isFinite(ounces) && ounces > 0 ? ounces : null;
}

function sizeText(sizeValue: string, sizeUnit: string): string {
  const unit = sizeUnit === "l" ? "L" : sizeUnit;
  return `${Number(sizeValue)} ${unit}`;
}

/** The shipped liquor categories have labels; a category a location added
 * itself is only a stored key ("cans_and_bottles"), so say it in words. */
function categoryName(key: string): string {
  const known = categoryLabel(key);
  if (known !== key) {
    return known;
  }
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.length === 0 ? key : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function classifyItem(product: LiquorProductDto): StockItem {
  const categoryDisplay = categoryName(product.category);
  const base = {
    id: product.id,
    name: product.name,
    categoryKey: product.category,
    categoryName: categoryDisplay,
  };
  // The count walk's own rule: a unit price marks a whole-unit item.
  if (product.unit_price_cents !== null) {
    return {
      ...base,
      kind: "unit",
      containerWord: "",
      containerUnit: null,
      sizeLabel: "",
      ouncesPerContainer: null,
      meta: `${categoryDisplay} · sold by the unit`,
    };
  }
  // The one shared rule: a keg's keg size volume, otherwise Size.
  // KEGMATH1: the server sends a keg's volume on the row (default_keg_size_volume_oz); a keg without it is ounces only.
  const ounces = containerOunces(
    product,
    product.default_keg_size !== null && product.default_keg_size_volume_oz != null
      ? new Map([[product.default_keg_size, product.default_keg_size_volume_oz]])
      : new Map<string, string>(),
  );
  if (ounces !== null && product.container_type !== null) {
    const keg = product.container_type === "keg";
    const label = keg
      ? `${Math.round(ounces)} oz`
      : sizeText(product.size_value ?? "", product.size_unit ?? "");
    return {
      ...base,
      kind: keg ? "keg" : "bottle",
      containerWord: product.container_type,
      containerUnit: product.container_type,
      sizeLabel: label,
      ouncesPerContainer: ounces,
      meta: `${categoryDisplay} · ${label} ${product.container_type}`,
    };
  }
  return {
    ...base,
    kind: "unsized",
    containerWord: "",
    containerUnit: null,
    sizeLabel: "",
    ouncesPerContainer: null,
    meta: `${categoryDisplay} · no bottle size yet`,
  };
}

// ---------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------

export type Entry =
  | { type: "zero" }
  | { type: "container"; whole: number; tenth: number }
  | { type: "units"; units: number }
  | { type: "ounces"; text: string };

function entryFits(item: StockItem, entry: Entry): boolean {
  switch (entry.type) {
    case "zero":
      return true;
    case "container":
      return item.kind === "bottle" || item.kind === "keg";
    case "units":
      return item.kind === "unit";
    case "ounces":
      return item.kind === "unsized";
  }
}

/** True only for a deliberate entry that fits the row. A blank is never an
 * entry, and never zero: zero is its own button. */
export function isEntered(item: StockItem, entry: Entry | undefined): boolean {
  if (entry === undefined || !entryFits(item, entry)) {
    return false;
  }
  if (entry.type === "ounces") {
    return QUANTITY_PATTERN.test(normalizeLeadingDot(entry.text));
  }
  return true;
}

function trimmedNumber(value: number): string {
  const rounded = Number(value.toFixed(4));
  return Number.isInteger(rounded) || String(rounded).split(".")[1]?.length === 1
    ? rounded.toFixed(1)
    : String(rounded);
}

/** The ounces box with a button pressed: floored at zero, an unreadable box
 * starts again from zero, and precision the person typed is kept. */
export function adjustOunces(text: string, delta: number): string {
  const trimmed = normalizeLeadingDot(text);
  const current = QUANTITY_PATTERN.test(trimmed) ? Number(trimmed) : 0;
  return trimmedNumber(Math.max(0, Number((current + delta).toFixed(4))));
}

export function formatOunces(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function containerNumber(entry: { whole: number; tenth: number }): number {
  return entry.whole + entry.tenth / 10;
}

export interface EntryDisplay {
  text: string;
  sub: string;
  /** Ounces when known, for the live line under a tenths control. */
  ounces: number | null;
}

export function entryDisplay(item: StockItem, entry: Entry | undefined): EntryDisplay | null {
  if (!isEntered(item, entry) || entry === undefined) {
    return null;
  }
  switch (entry.type) {
    case "zero":
      return {
        text: "None",
        sub: item.kind === "keg" ? "0 kegs" : item.kind === "unit" ? "0 units" : "0 oz",
        ounces: 0,
      };
    case "container": {
      const n = containerNumber(entry);
      const word = n === 1 ? item.containerWord : `${item.containerWord}s`;
      const ounces = item.ouncesPerContainer === null ? null : n * item.ouncesPerContainer;
      return {
        text: `${n.toFixed(1)} ${word}`,
        sub: ounces === null ? "" : `${formatOunces(ounces)} oz`,
        ounces,
      };
    }
    case "units":
      return {
        text: `${entry.units} ${entry.units === 1 ? "unit" : "units"}`,
        sub: "whole units",
        ounces: null,
      };
    case "ounces": {
      const n = Number(entry.text.trim());
      return { text: `${trimmedNumber(n)} oz`, sub: "ounces only", ounces: n };
    }
  }
}

// ---------------------------------------------------------------------
// The request
// ---------------------------------------------------------------------

export function idempotencyKeyFor(batchId: string, liquorProductId: string): string {
  // The notes sketch colons; the server's key rule (letters, digits, "-" and
  // "_", 1 to 100 characters) refuses them, so this is the form that works.
  return `opening_${batchId}_${liquorProductId}`;
}

export interface OpeningBalanceRequest {
  idempotencyKey: string;
  liquorProductId: string;
  quantity: string;
  unit: string;
  note: string;
}

function ownUnit(item: StockItem): string {
  switch (item.kind) {
    case "bottle":
    case "keg":
      return item.containerUnit ?? "oz";
    case "unit":
      return "unit";
    case "unsized":
      return "oz";
  }
}

export function buildRequest(
  item: StockItem,
  entry: Entry,
  batchId: string,
): OpeningBalanceRequest {
  if (!isEntered(item, entry)) {
    throw new Error(`no deliberate entry to send for ${item.name}`);
  }
  let quantity: string;
  switch (entry.type) {
    case "zero":
      quantity = "0";
      break;
    case "container":
      quantity = combineWholeBottlesAndTenths(entry.whole, entry.tenth);
      break;
    case "units":
      quantity = String(entry.units);
      break;
    case "ounces":
      quantity = entry.text.trim();
      break;
  }
  return {
    idempotencyKey: idempotencyKeyFor(batchId, item.id),
    liquorProductId: item.id,
    quantity,
    unit: ownUnit(item),
    note: OPENING_BALANCE_NOTE,
  };
}

// STARTSTOCK1 (DEC-601): the Add item sheet's "On hand now" field. These two functions only decide the unit word and read
// the typed text into the same Entry Set starting stock builds; the request itself is still buildRequest above.

/** What a not-yet-saved item will be counted in, decided from the form the same way classifyItem decides it from a saved
 * item: a whole-unit item counts units, a sized container counts containers, anything else counts ounces. */
export function startStockKindFor(form: {
  wholeUnit: boolean;
  containerType: string;
  sizeValue: string;
  sizeUnit: string;
  defaultKegSize?: string;
  kegSizeVolumeOzByValue?: ReadonlyMap<string, string>;
}): { kind: StockKind; word: string } {
  if (form.wholeUnit) {
    return { kind: "unit", word: "units" };
  }
  const container = form.containerType.trim();
  const sizeValue = form.sizeValue.trim();
  const sizeUnit = form.sizeUnit.trim().toLowerCase();
  const ounces =
    container.length === 0
      ? null
      : containerOunces(
          {
            container_type: container,
            size_value: sizeValue.length > 0 ? sizeValue : null,
            size_unit: sizeUnit.length > 0 ? sizeUnit : null,
            default_keg_size:
              form.defaultKegSize !== undefined && form.defaultKegSize.length > 0
                ? form.defaultKegSize
                : null,
          },
          form.kegSizeVolumeOzByValue ?? new Map<string, string>(),
        );
  if (ounces !== null) {
    return { kind: container === "keg" ? "keg" : "bottle", word: `${container}s` };
  }
  return { kind: "unsized", word: "oz" };
}

/** Reads the typed amount. null is a blank (send nothing); "invalid" is text the unit cannot take. Containers take
 * tenths, the same increments Count stock offers. */
export function parseStartStockText(kind: StockKind, raw: string): Entry | null | "invalid" {
  const text = normalizeLeadingDot(raw);
  if (text.length === 0) {
    return null;
  }
  if (kind === "bottle" || kind === "keg") {
    const match = /^(\d+)(?:\.(\d))?$/.exec(text);
    return match === null
      ? "invalid"
      : { type: "container", whole: Number(match[1]), tenth: Number(match[2] ?? "0") };
  }
  if (kind === "unit") {
    return /^\d+$/.test(text) ? { type: "units", units: Number(text) } : "invalid";
  }
  return QUANTITY_PATTERN.test(text) ? { type: "ounces", text } : "invalid";
}

export { BOTTLE_TENTH_OPTIONS };

// ---------------------------------------------------------------------
// Filters, groups and counts
// ---------------------------------------------------------------------

export type StatusById = Readonly<Record<string, OpeningBalanceHistory>>;
export type StatusFilter = "all" | "left" | "entered" | "done";

export interface ListFilter {
  q: string;
  category: string;
  status: StatusFilter;
}

function hasHistory(statusById: StatusById, id: string): boolean {
  const history = statusById[id];
  return history !== undefined && history !== "none";
}

export interface CountSummary {
  total: number;
  alreadySet: number;
  entered: number;
  left: number;
}

export function countSummary(
  items: readonly StockItem[],
  statusById: StatusById,
  entries: ReadonlyMap<string, Entry>,
): CountSummary {
  let alreadySet = 0;
  let entered = 0;
  let left = 0;
  for (const item of items) {
    if (hasHistory(statusById, item.id)) {
      alreadySet += 1;
    } else if (isEntered(item, entries.get(item.id))) {
      entered += 1;
    } else {
      left += 1;
    }
  }
  return { total: items.length, alreadySet, entered, left };
}

export function visibleItems(
  items: readonly StockItem[],
  statusById: StatusById,
  entries: ReadonlyMap<string, Entry>,
  filter: ListFilter,
): StockItem[] {
  const query = filter.q.trim().toLowerCase();
  return items.filter((item) => {
    if (filter.category !== "all" && item.categoryKey !== filter.category) {
      return false;
    }
    if (query.length > 0 && !`${item.name} ${item.categoryName}`.toLowerCase().includes(query)) {
      return false;
    }
    const done = hasHistory(statusById, item.id);
    const entered = !done && isEntered(item, entries.get(item.id));
    switch (filter.status) {
      case "left":
        return !done && !entered;
      case "entered":
        return entered;
      case "done":
        return done;
      case "all":
        return true;
    }
  });
}

export interface CategoryGroup {
  categoryKey: string;
  categoryName: string;
  items: StockItem[];
  /** Items in this group with an entry that will be written. */
  entered: number;
  /** Items in this group that can still be set (no history). */
  open: number;
}

export function groupByCategory(
  items: readonly StockItem[],
  statusById: StatusById,
  entries: ReadonlyMap<string, Entry>,
): CategoryGroup[] {
  const groups = new Map<string, CategoryGroup>();
  const ordered = [...items].sort(
    (a, b) => compareNames(a.categoryName, b.categoryName) || compareNames(a.name, b.name),
  );
  for (const item of ordered) {
    let group = groups.get(item.categoryKey);
    if (group === undefined) {
      group = {
        categoryKey: item.categoryKey,
        categoryName: item.categoryName,
        items: [],
        entered: 0,
        open: 0,
      };
      groups.set(item.categoryKey, group);
    }
    group.items.push(item);
    if (!hasHistory(statusById, item.id)) {
      group.open += 1;
      if (isEntered(item, entries.get(item.id))) {
        group.entered += 1;
      }
    }
  }
  return [...groups.values()];
}

// ---------------------------------------------------------------------
// The draft: held per tenant and per person on the device
// ---------------------------------------------------------------------

export function draftKey(tenantId: string, userId: string): string {
  return `starting-stock-draft:${tenantId}:${userId}`;
}

export function serializeDraft(entries: ReadonlyMap<string, Entry>): string {
  return JSON.stringify({ v: 1, entries: Object.fromEntries(entries) });
}

function parseEntry(raw: unknown): Entry | undefined {
  if (typeof raw !== "object" || raw === null) {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  switch (record["type"]) {
    case "zero":
      return { type: "zero" };
    case "container": {
      const whole = record["whole"];
      const tenth = record["tenth"];
      if (
        typeof whole === "number" &&
        typeof tenth === "number" &&
        Number.isInteger(whole) &&
        whole >= 0 &&
        whole <= 99999 &&
        Number.isInteger(tenth) &&
        tenth >= 0 &&
        tenth <= 9
      ) {
        return { type: "container", whole, tenth };
      }
      return undefined;
    }
    case "units": {
      const units = record["units"];
      if (typeof units === "number" && Number.isInteger(units) && units >= 0 && units <= 999999) {
        return { type: "units", units };
      }
      return undefined;
    }
    case "ounces": {
      const text = record["text"];
      if (typeof text === "string" && /^[0-9.]{0,12}$/.test(text)) {
        return { type: "ounces", text };
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

/** Reads a stored draft. Anything unreadable or malformed yields an empty or
 * smaller draft, never an exception and never a trusted junk value. */
export function parseDraft(raw: string | null): Map<string, Entry> {
  const entries = new Map<string, Entry>();
  if (raw === null) {
    return entries;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return entries;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return entries;
  }
  const envelope = parsed as Record<string, unknown>;
  const stored = envelope["entries"];
  if (envelope["v"] !== 1 || typeof stored !== "object" || stored === null) {
    return entries;
  }
  for (const [id, value] of Object.entries(stored as Record<string, unknown>)) {
    const entry = parseEntry(value);
    if (entry !== undefined) {
      entries.set(id, entry);
    }
  }
  return entries;
}

export interface CleanedDraft {
  kept: Map<string, Entry>;
  /** Dropped because the item gained history (or already has starting stock). */
  droppedForHistory: number;
  /** Dropped because the item is no longer listed or the entry no longer fits it. */
  droppedOther: number;
}

export function cleanDraft(
  draft: ReadonlyMap<string, Entry>,
  items: readonly StockItem[],
  statusById: StatusById,
): CleanedDraft {
  const byId = new Map(items.map((item) => [item.id, item]));
  const kept = new Map<string, Entry>();
  let droppedForHistory = 0;
  let droppedOther = 0;
  for (const [id, entry] of draft) {
    const item = byId.get(id);
    if (item === undefined) {
      droppedOther += 1;
    } else if (hasHistory(statusById, id)) {
      droppedForHistory += 1;
    } else if (!entryFits(item, entry)) {
      droppedOther += 1;
    } else {
      kept.set(id, entry);
    }
  }
  return { kept, droppedForHistory, droppedOther };
}

// ---------------------------------------------------------------------
// What each server answer means, per item
// ---------------------------------------------------------------------

export type SaveOutcomeKind = "saved" | "locked" | "fix" | "stop" | "unavailable" | "retry";

export interface SaveOutcome {
  kind: SaveOutcomeKind;
  /** Plain words for the person, shown on the row. */
  message: string;
  /** A "no bottle size yet" answer: the row flips to the ounces-only editor. */
  reopenAsOunces?: boolean;
}

export const LOCKED_MESSAGE =
  "Someone sold, received or counted this item while you were entering. Its starting stock can no longer be set here. Use a count to correct it.";
export const RETRY_DROPPED_MESSAGE =
  "The connection dropped before this one saved. Nothing was written. Tap Try again.";
export const UNSIZED_SENTENCE =
  "This item has no bottle size yet. Set its size in Stock & vendor, or enter the amount in ounces.";

export function classifySaveResponse(result: {
  status: number;
  body?: { message?: string; error?: string } | undefined;
  networkError: boolean;
}): SaveOutcome {
  if (result.networkError) {
    return { kind: "retry", message: RETRY_DROPPED_MESSAGE };
  }
  const serverMessage = result.body?.message ?? "";
  if (/fenced/i.test(serverMessage)) {
    return {
      kind: "unavailable",
      message:
        "This location cannot save stock changes right now. Nothing was written. Try again in a few minutes.",
    };
  }
  if (result.status === 200 || result.status === 201) {
    return { kind: "saved", message: "" };
  }
  if (result.status === 401 || result.status === 403) {
    return {
      kind: "stop",
      message: "You no longer have permission to set starting stock. Nothing more was sent.",
    };
  }
  if (result.status === 400 && /already has inventory history/i.test(serverMessage)) {
    return { kind: "locked", message: LOCKED_MESSAGE };
  }
  if (result.status === 400 && /no bottle size yet/i.test(serverMessage)) {
    return { kind: "fix", message: UNSIZED_SENTENCE, reopenAsOunces: true };
  }
  if (result.status >= 500 || result.status === 0) {
    return {
      kind: "retry",
      message: "This one did not save. Tap Try again. Trying again cannot save it twice.",
    };
  }
  return {
    kind: "fix",
    message: serverMessage.length > 0 ? serverMessage : "The amount was not accepted.",
  };
}

// ---------------------------------------------------------------------
// The batch runner
// ---------------------------------------------------------------------

export interface RunBatchOptions {
  itemIds: readonly string[];
  /** Three to four in practice; each product locks only its own row, so
   * different products do not contend. */
  concurrency: number;
  /** Checked before every send: "Stop after this one". */
  shouldStop: () => boolean;
  send: (itemId: string) => Promise<SaveOutcome>;
  onStart?: (itemId: string) => void;
  onDone: (itemId: string, outcome: SaveOutcome) => void;
}

/** Sends one item at a time per worker, in the order given. A "stop" answer
 * (permission lost) halts every worker; Stop-after-this-one halts new sends
 * and lets the in-flight ones finish; a Locked or Try again item never stops
 * the rest. A send that throws is a Try again, never a crash. */
export async function runBatch(options: RunBatchOptions): Promise<void> {
  let next = 0;
  let halted = false;
  const worker = async (): Promise<void> => {
    while (!halted && !options.shouldStop()) {
      const index = next;
      next += 1;
      const itemId = options.itemIds[index];
      if (itemId === undefined) {
        return;
      }
      options.onStart?.(itemId);
      let outcome: SaveOutcome;
      try {
        outcome = await options.send(itemId);
      } catch {
        outcome = { kind: "retry", message: RETRY_DROPPED_MESSAGE };
      }
      if (outcome.kind === "stop") {
        halted = true;
      }
      options.onDone(itemId, outcome);
    }
  };
  const workers = Math.max(1, Math.min(options.concurrency, options.itemIds.length));
  await Promise.all(Array.from({ length: workers }, () => worker()));
}

// ---------------------------------------------------------------------
// The setup step
// ---------------------------------------------------------------------

/** Done when "this location starts empty" is set, or when active items exist
 * and none lacks history; pending otherwise; unknown without the permission or
 * when the read failed (never a false pending or done). */
export function startingStockStepState(params: {
  permitted: boolean;
  startsEmpty: boolean;
  /** INV38: the location's durable "initial stock is in" answer; once true the step is done for good. */
  initialStockCompleted?: boolean;
  products: readonly { liquorProductId: string; history: OpeningBalanceHistory }[] | undefined;
}): "done" | "pending" | "unknown" {
  if (!params.permitted || params.products === undefined) {
    return "unknown";
  }
  if (params.initialStockCompleted === true) {
    return "done";
  }
  if (params.startsEmpty) {
    return "done";
  }
  return params.products.length > 0 && params.products.every((row) => row.history !== "none")
    ? "done"
    : "pending";
}
